// Misconception routing + remediation + walkback. Phase E.
//
// Spec §"Misconception and remediation logic" (lines 1176–1293).
// The same-error rule is non-negotiable: if the same errorType or
// misconceptionId appears in at least 2 of the last 3 relevant
// attempts, the engine must NOT serve more of the same direct-practice
// item. This module produces:
//   - the rich MisconceptionSignal that routing consumes
//   - intervention lookup against misconceptionInterventions.json
//   - walkback target derivation (helper for Phase F's ProgressionState)
//   - the 3-item remediation run plan
//   - the exit predicate (when can the kid leave remediation?)
//
// misconceptionInterventions.json ships empty (§E-Q1, same fail-closed
// posture as Phase D §D-Q1). selectMisconceptionIntervention returns
// null for every misconception in production until expert content
// lands. Phase F surfaces this gap in the decision-engine output.

import type {
  ChildAttempt,
  ErrorType,
  HintLevel,
  MisconceptionInterventionEntry,
  MisconceptionSignal,
  NextActivityPlan,
  ProgressionState,
} from "./types";
import type { LearningProgression } from "../curriculum/types/manifests";

// Mirrors HINT_ORDER from supportModeGating.ts (kept local per
// WORKING_AGREEMENT no-Phase-C-touch rule; consolidate in post-Phase-F
// cleanup — see FOLLOWUPS.md).
//
// Exported in Phase F2 so adaptationDecisionEngine.ts can reuse this
// copy instead of adding a 4th private one. Per §F2-Q2: avoids
// further duplication until Phase G consolidates into
// src/adaptation/constants.ts.
export const HINT_ORDER: Record<HintLevel, number> = {
  H0: 0,
  H1: 1,
  H2: 2,
  H3: 3,
  H4: 4,
};

/**
 * Detect a same-error loop in the last 3 relevant attempts. Counts
 * BOTH misconceptionId AND errorType; takes the dominant count of
 * either. Returns a rich MisconceptionSignal that the decision
 * engine consumes for routing.
 *
 * sameErrorLoop = max(misconceptionCount, errorTypeCount) >= 2.
 * Severity bands: none / moderate (count=2) / high (count=3).
 */
// errorType values that carry no diagnostic signal on their own — the
// unclassified default (buildAttempt.ts's fallback when no authored/
// heuristic classification fires) and "asked for help" (never a
// misconception). Counting either toward the same-error-loop would fire
// remediation off two UNRELATED wrong answers that just never got
// classified, not off a real repeated misconception (MA-381 item e).
const NON_DIAGNOSTIC_ERROR_TYPES: ReadonlySet<ErrorType> = new Set([
  "incorrect_answer",
  "i_dont_know",
]);

// MA-809 (operator ruling, RED round 1 SHOULD-2): the four part-whole types
// are RECORDED (attempt row, export, analytics) but do not count toward the
// same-error loop. The only walk-back target for ms.math.05 is ms.math.04
// (number recognition), which teaches neither part-whole nor counting; they
// rejoin the trigger once walk-back targets are error-aware (follow-up filed
// by the operator).
export const NON_LOOPING_ERROR_TYPES: ReadonlySet<ErrorType> = new Set([
  "missing_addend_used_whole",
  "missing_addend_repeated_part",
  "missing_addend_off_by_one",
  "missing_addend_empty_part",
]);

export function detectSameErrorLoop(attempts: ChildAttempt[]): MisconceptionSignal {
  const relevant = attempts.slice(-3);
  const misconceptionCounts = new Map<string, number>();
  const errorCounts = new Map<string, number>();
  // MA-809 (operator ruling, RED round 1 MUST-1, option b): an errorType
  // counts at most ONCE per (item, session) in the window — a wrong tap and a
  // wrong retry on the same item in one session are one occurrence. The same
  // miss on the same item in two different sessions still counts twice
  // (genuine repetition; keeps MA-725's apple-coach reach). An attempt with
  // no itemId (an in-session accumulator entry — the itemId gap is a
  // follow-up filed by the operator) counts on its own, as before.
  const countedErrorItems = new Set<string>();

  for (const attempt of relevant) {
    if (attempt.misconceptionId) {
      misconceptionCounts.set(
        attempt.misconceptionId,
        (misconceptionCounts.get(attempt.misconceptionId) ?? 0) + 1,
      );
    }
    if (
      attempt.errorType &&
      !NON_DIAGNOSTIC_ERROR_TYPES.has(attempt.errorType) &&
      !NON_LOOPING_ERROR_TYPES.has(attempt.errorType)
    ) {
      if (attempt.itemId) {
        const key = `${attempt.errorType}\u0000${attempt.itemId}\u0000${attempt.sessionId ?? ""}`;
        if (countedErrorItems.has(key)) continue;
        countedErrorItems.add(key);
      }
      errorCounts.set(
        attempt.errorType,
        (errorCounts.get(attempt.errorType) ?? 0) + 1,
      );
    }
  }

  const dominantMisconception = [...misconceptionCounts.entries()].sort(
    (a, b) => b[1] - a[1],
  )[0] ?? null;
  const dominantError = [...errorCounts.entries()].sort((a, b) => b[1] - a[1])[0] ?? null;

  const countInLast3 = Math.max(
    dominantMisconception?.[1] ?? 0,
    dominantError?.[1] ?? 0,
  );
  const sameErrorLoop = countInLast3 >= 2;

  const severity: MisconceptionSignal["severity"] = !sameErrorLoop
    ? "none"
    : countInLast3 === 2
      ? "moderate"
      : "high";

  return {
    skillId: relevant[relevant.length - 1]?.skillId ?? "",
    misconceptionId: dominantMisconception?.[0] ?? null,
    errorType: (dominantError?.[0] as ErrorType | undefined) ?? null,
    sameErrorLoop,
    countInLast3,
    severity,
    recommendation: sameErrorLoop ? "remediate" : "none",
    interventionId: dominantMisconception?.[0] ?? null,
    walkbackSkillId: null, // Phase F's decision engine fills this from selectWalkbackSkill
  };
}

/**
 * Look up an intervention entry by misconception ID. Returns null
 * when misconceptionId is null OR not present in the map. Phase E
 * ships with misconceptionInterventions.json empty (§E-Q1), so this
 * returns null in production until expert content lands.
 */
export function selectMisconceptionIntervention(
  misconceptionId: string | null,
  map: MisconceptionInterventionEntry[],
): MisconceptionInterventionEntry | null {
  if (!misconceptionId) return null;
  return map.find((entry) => entry.misconceptionId === misconceptionId) ?? null;
}

/**
 * Pick the walkback target for a skill. Returns the most-recent
 * prerequisite from `progressionState.walkbackSkillIds[0]`. The spec
 * (lines 1241–1242) treats walkback the same with and without a
 * misconceptionId today; that branch is reserved for misconception-
 * specific walkback routing in a future phase.
 */
export function selectWalkbackSkill(
  _skillId: string,
  _misconceptionId: string | null,
  progressionState: ProgressionState,
): string | null {
  return progressionState.walkbackSkillIds[0] ?? null;
}

/**
 * Helper for Phase F's ProgressionState builder.
 *
 * Given a microSkill ID + the loaded learningProgressions manifest,
 * returns the prerequisite chain in REVERSE (most-recent prereq first).
 * Phase F uses this when constructing ProgressionState.walkbackSkillIds.
 * Phase E's selectWalkbackSkill then just reads [0] off that array.
 *
 * Lives in misconceptionRouting.ts (not Phase F's future module)
 * because the walkback logic conceptually belongs next to its
 * routing consumer per §E-Q3.
 */
export function deriveWalkbackSkillIds(
  microSkillId: string,
  progressions: LearningProgression[],
): string[] {
  const entry = progressions.find((p) => p.microSkillId === microSkillId);
  if (!entry) return [];
  // Most-recent prerequisite first: reverse the array. The
  // learningProgressions.json prerequisites are authored in dependency
  // order (the kid encountered the first one earliest, the last one
  // most recently before reaching the current skill).
  return [...entry.prerequisites].reverse();
}

/**
 * Build the NextActivityPlan for a 3-item remediation run.
 * Verbatim from spec lines 1245–1271. Allowed supports include H1-H3
 * (the spec deliberately permits scaffolded remediation); H4 stays
 * blocked because it teaches rather than certifies. requireIndependent-
 * Retest = true so the engine forces a clean retest after remediation.
 */
export function planRemediationRun(
  skillId: string,
  misconceptionSignal: MisconceptionSignal,
  _progressionState: ProgressionState,
): NextActivityPlan {
  return {
    decision: "remediate",
    skillId,
    nextSkillId: null,
    selectedActivityType: "remediationContrast",
    selectedContentFile: "/src/curriculum/data/manifests/misconceptionInterventions.json",
    selectedContentId: misconceptionSignal.interventionId,
    supportPlan: {
      allowedSupports: ["independent", "H1", "H2", "H3"],
      initialHintLevel: "H1",
      requireIndependentRetest: true,
      separateHandwritingChannel: true,
    },
    runPlan: {
      maxItems: 3,
      includeReviewCheckpoint: false,
      includeTransferTask: true,
      includeDelayedReviewItem: false,
    },
    scheduleFollowUpAt: null,
  };
}

/**
 * Can the kid exit the remediation loop?
 *
 * True iff the last 3 remediation attempts include:
 *   - ≥2 correct at hint level ≤ H2, AND
 *   - ≥1 transfer-task attempt that passed.
 *
 * Per spec line 1276–1278. H3+ correct does NOT count toward exit
 * (still under heavy scaffold).
 */
export function evaluateRemediationExit(
  remediationAttempts: ChildAttempt[],
): boolean {
  const recent = remediationAttempts.slice(-3);
  if (recent.length < 3) return false;
  const successful = recent.filter(
    (a) => a.correct && HINT_ORDER[a.hintLevel] <= HINT_ORDER.H2,
  ).length;
  const freshTransferPassed = recent.some((a) => a.isTransferTask && a.correct);
  return successful >= 2 && freshTransferPassed;
}
